import { useCorp } from "@highway/design-system";
import { dataFetcher } from "@highway/hwy-design-system";
import { sub } from "date-fns";
import { useRouter } from "next/navigation";

import { useLayoutEffect, useState } from "react";
import { useSearchParams } from "next/navigation";
import { IArticle } from "../../types/global-features";

export const useArticle = ({
    baseRoute,
    checkCookie = false,
    getDisclaimer = false,
}: {
    baseRoute: string;
    checkCookie?: boolean;
    getDisclaimer?: boolean;
}) => {
    const searchParams = useSearchParams();

    const [viewedArticle, setViewedArticle] = useState<Partial<IArticle> | undefined>();
    const [archivedArticles, setArchivedArticles] = useState<Partial<IArticle>[]>([]);
    const today = new Date();
    const [selectedDate, setSelectedDate] = useState(`${today.getMonth() + 1}-${today.getFullYear()}`);
    const [disclaimer, setDisclaimer] = useState("");
    const { corp } = useCorp();
    const [isViewMoreVisible, setIsViewMoreVisible] = useState(true);
    const [isCookieSet, setIsCookieSet] = useState(false);
    const pastArticleId = searchParams?.get("articleID");
    const router = useRouter();

    useLayoutEffect(() => {
        getData();
    }, [pastArticleId]);

    useLayoutEffect(() => {
        if (corp.id && getDisclaimer) {
            dataFetcher(`/v1/user/corp-stance-disclaimer?corpID=${corp.id}`).then((disclaimerResponse) => {
                setDisclaimer(disclaimerResponse.data[0].textPanelContent);
            });
        }
    }, [corp.id]);

    const getData = () => {
        if (checkCookie) {
            dataFetcher(`/v1/signed-cookies`, {
                credentials: "include",
            })
                .then(() => {
                    setIsCookieSet(true);
                })
                .catch(() => {});
        }
        if (pastArticleId) {
            dataFetcher(`/v1/${baseRoute}/${pastArticleId}`).then((latestArticleResponse) => {
                if (!latestArticleResponse.data[0]) {
                    router.push("/404");
                } else {
                    setViewedArticle(latestArticleResponse.data[0]);
                }
            });
        } else {
            dataFetcher(`/v1/${baseRoute}/latest`).then((latestArticleResponse) => {
                setViewedArticle(latestArticleResponse.data[0]);
            });
        }
        getArchivedArticles();
    };

    const getArchivedArticles = async () => {
        const today = new Date();
        const thisMonthArticlesResponse = await dataFetcher(
            `/v1/${baseRoute}?month=${today.getMonth() + 1}&year=${today.getFullYear()}`,
        );
        if (thisMonthArticlesResponse.data.length) {
            setArchivedArticles(thisMonthArticlesResponse.data);
            if (thisMonthArticlesResponse.data.length < 5) setIsViewMoreVisible(false);
        } else {
            const lastMonth = sub(today, { months: 1 });
            const lastMonthArticlesResponse = await dataFetcher(
                `/v1/${baseRoute}?month=${lastMonth.getMonth() + 1}&year=${lastMonth.getFullYear()}`,
            );
            if (lastMonthArticlesResponse.data.length) {
                setArchivedArticles(lastMonthArticlesResponse.data);
                setSelectedDate(`${lastMonth.getMonth() + 1}-${lastMonth.getFullYear()}`);
                if (lastMonthArticlesResponse.data.length < 5) setIsViewMoreVisible(false);
            }
        }
    };

    const handleViewMore = async () => {
        const [month, year] = selectedDate.split("-");
        const response = await dataFetcher(
            `/v1/${baseRoute}?month=${month}&year=${year}&offset=${archivedArticles.length}`,
        );
        setArchivedArticles([...archivedArticles, ...response.data]);
        if (response.data.length < 5) setIsViewMoreVisible(false);
    };

    const handleChangeDate = async (value: string) => {
        setSelectedDate(value);
        const [month, year] = value.split("-");
        const response = await dataFetcher(`/v1/${baseRoute}?month=${month}&year=${year}`);
        setArchivedArticles(response.data);
        setIsViewMoreVisible(response.data.length === 5);
    };

    return {
        getData,
        handleViewMore,
        handleChangeDate,
        viewedArticle,
        archivedArticles,
        selectedDate,
        disclaimer,
        isViewMoreVisible,
        isCookieSet,
    };
};
